import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { speedToColor, getLegendGradientCSS } from '../utils/heatmapColors';
import { ALL_CHANNELS, CHANNELS } from '../utils/constants';

const TRACE_COLORS = [
  '#2e86c1', '#e74c3c', '#27ae60', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#00bcd4',
];

const ESRI_TILE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

// Downsample points to at most maxPts, preserving first and last
function downsample(pts, maxPts) {
  if (pts.length <= maxPts) return pts;
  const step = (pts.length - 1) / (maxPts - 1);
  const out = [];
  for (let i = 0; i < maxPts - 1; i++) {
    out.push(pts[Math.round(i * step)]);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/**
 * Inner component that draws polyline layers and manages map bounds.
 * Uses a single shared canvas renderer for all polylines.
 */
function MapLayers({ laps, dataKey, carPositions, focusBounds }) {
  const map = useMap();
  const layerGroupRef = useRef(null);
  const markerGroupRef = useRef(null);
  const rendererRef = useRef(null);
  const didFitRef = useRef(false);
  const prevLapsRef = useRef(null);

  // Get or create a single shared canvas renderer
  function getRenderer() {
    if (!rendererRef.current) {
      rendererRef.current = L.canvas({ padding: 0.5 });
    }
    return rendererRef.current;
  }

  // Build all lap path data (coordinates + colors)
  const lapPaths = useMemo(() => {
    if (!laps?.length) return [];

    // Compute global min/max across ALL laps for consistent heatmap colors
    let gMin = Infinity, gMax = -Infinity;
    for (const lap of laps) {
      if (!lap.data?.length) continue;
      for (const pt of lap.data) {
        const v = pt[dataKey];
        if (v != null) {
          if (v < gMin) gMin = v;
          if (v > gMax) gMax = v;
        }
      }
    }
    if (!isFinite(gMin)) { gMin = 0; gMax = 1; }

    const paths = [];

    for (let li = 0; li < laps.length; li++) {
      const lap = laps[li];
      const raw = lap.data;
      if (!raw?.length) continue;

      // Filter to valid GPS points, downsample to max 500 per lap
      const valid = raw.filter(pt => pt.lat != null && pt.lng != null);
      const pts = downsample(valid, 500);
      if (pts.length < 2) continue;

      const latlngs = pts.map(pt => [pt.lat, pt.lng]);

      // Build per-segment heatmap colors using global range
      const segColors = [];
      for (let i = 0; i < pts.length - 1; i++) {
        const val = pts[i][dataKey] ?? 0;
        segColors.push(speedToColor(val, gMin, gMax));
      }

      paths.push({ latlngs, segColors });
    }

    return paths;
  }, [laps, dataKey]);

  // Draw all lap paths on the map
  useEffect(() => {
    if (!map) return;

    if (layerGroupRef.current) {
      map.removeLayer(layerGroupRef.current);
      layerGroupRef.current = null;
    }

    if (!lapPaths.length) return;

    const renderer = getRenderer();
    const group = L.layerGroup();
    for (const path of lapPaths) {
      // Draw heatmap-colored segments for every lap
      for (let i = 0; i < path.latlngs.length - 1; i++) {
        L.polyline(
          [path.latlngs[i], path.latlngs[i + 1]],
          { color: path.segColors[i], weight: 3, opacity: 0.9, renderer }
        ).addTo(group);
      }
    }

    group.addTo(map);
    layerGroupRef.current = group;

    return () => {
      if (layerGroupRef.current) {
        map.removeLayer(layerGroupRef.current);
        layerGroupRef.current = null;
      }
    };
  }, [map, lapPaths]);

  // Fit map bounds when laps change
  useEffect(() => {
    if (!map || !lapPaths.length) return;
    if (prevLapsRef.current === laps && didFitRef.current) return;

    const allPts = [];
    for (const path of lapPaths) {
      for (const ll of path.latlngs) allPts.push(ll);
    }
    if (allPts.length > 1) {
      map.fitBounds(L.latLngBounds(allPts), { padding: [30, 30] });
    }
    prevLapsRef.current = laps;
    didFitRef.current = true;
  }, [map, lapPaths, laps]);

  // Zoom to focusBounds when user drag-selects on telemetry graph
  useEffect(() => {
    if (!map || !focusBounds) return;
    const { minLat, maxLat, minLng, maxLng } = focusBounds;
    if (minLat != null && maxLat != null && minLng != null && maxLng != null) {
      map.fitBounds([[minLat, minLng], [maxLat, maxLng]], { padding: [30, 30] });
    }
  }, [map, focusBounds]);

  // Car arrow markers (matching the heatmap's racecar arrow icons)
  useEffect(() => {
    if (!map) return;

    if (markerGroupRef.current) {
      map.removeLayer(markerGroupRef.current);
      markerGroupRef.current = null;
    }

    if (!carPositions?.length) return;

    const channelInfo = CHANNELS[dataKey] || CHANNELS.speed;
    const unit = channelInfo.unit || '';

    const group = L.layerGroup();
    for (const pos of carPositions) {
      if (pos.lat == null || pos.lng == null) continue;

      // Compute heading angle from current point to next point
      let angleDeg = 0;
      if (pos.nextLat != null && pos.nextLng != null) {
        const dLng = pos.nextLng - pos.lng;
        const dLat = pos.nextLat - pos.lat;
        angleDeg = (Math.atan2(dLng, dLat) * 180 / Math.PI);
      }

      const color = pos.color || '#2e86c1';
      const size = 28;

      // SVG arrow icon matching the heatmap's racecar shape
      const svg = `<svg width="${size}" height="${size}" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(14,14) rotate(${angleDeg})">
          <polygon points="0,-10 6,7 0,3 -6,7" fill="${color}" stroke="#fff" stroke-width="1.5"/>
          <circle cx="0" cy="-3" r="2" fill="rgba(255,255,255,0.5)"/>
        </g>
        <circle cx="14" cy="14" r="13" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.4"/>
      </svg>`;

      const icon = L.divIcon({
        html: svg,
        className: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });

      L.marker([pos.lat, pos.lng], { icon }).addTo(group);

      // Data label showing current channel value next to the car arrow
      if (pos.point) {
        const val = pos.point[dataKey];
        if (val != null) {
          const decimals = Math.abs(val) < 10 ? 1 : 0;
          const text = `${val.toFixed(decimals)}${unit ? ' ' + unit : ''}`;
          const label = L.divIcon({
            html: `<div style="
              white-space: nowrap;
              font-family: sans-serif;
              font-size: 11px;
              font-weight: 700;
              color: #fff;
              background: rgba(0,0,0,0.7);
              border: 1px solid ${color};
              border-radius: 3px;
              padding: 1px 5px;
              pointer-events: none;
            ">${text}</div>`,
            className: '',
            iconSize: [0, 0],
            iconAnchor: [-18, 14],
          });
          L.marker([pos.lat, pos.lng], { icon: label, interactive: false }).addTo(group);
        }
      }
    }
    group.addTo(map);
    markerGroupRef.current = group;

    return () => {
      if (markerGroupRef.current) {
        map.removeLayer(markerGroupRef.current);
        markerGroupRef.current = null;
      }
    };
  }, [map, carPositions]);

  return null;
}

export default function SatelliteMap({
  laps,
  carPositions,
  focusBounds,
  availableChannels = [],
  dataKey = 'speed',
  onDataKeyChange,
  mirrorX = false,
  mirrorY = false,
}) {
  // Compute GPS center for mirroring
  const gpsCenter = useMemo(() => {
    if (!mirrorX && !mirrorY) return null;
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const lap of (laps || [])) {
      for (const pt of (lap.data || [])) {
        if (pt.lat != null && pt.lng != null) {
          if (pt.lat < minLat) minLat = pt.lat;
          if (pt.lat > maxLat) maxLat = pt.lat;
          if (pt.lng < minLng) minLng = pt.lng;
          if (pt.lng > maxLng) maxLng = pt.lng;
        }
      }
    }
    if (!isFinite(minLat)) return null;
    return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
  }, [laps, mirrorX, mirrorY]);

  const mirrorPt = (lat, lng) => {
    if (!gpsCenter) return [lat, lng];
    const mLat = mirrorY ? 2 * gpsCenter.lat - lat : lat;
    const mLng = mirrorX ? 2 * gpsCenter.lng - lng : lng;
    return [mLat, mLng];
  };

  // Apply mirroring to laps for map rendering
  const mirroredLaps = useMemo(() => {
    if (!mirrorX && !mirrorY) return laps;
    if (!gpsCenter || !laps?.length) return laps;
    return laps.map((lap) => ({
      ...lap,
      data: (lap.data || []).map((pt) => {
        if (pt.lat == null || pt.lng == null) return pt;
        const [mLat, mLng] = mirrorPt(pt.lat, pt.lng);
        return { ...pt, lat: mLat, lng: mLng };
      }),
    }));
  }, [laps, gpsCenter, mirrorX, mirrorY]);

  // Apply mirroring to car positions
  const mirroredCarPositions = useMemo(() => {
    if (!mirrorX && !mirrorY) return carPositions;
    if (!gpsCenter || !carPositions?.length) return carPositions;
    return carPositions.map((pos) => {
      if (pos.lat == null || pos.lng == null) return pos;
      const [mLat, mLng] = mirrorPt(pos.lat, pos.lng);
      const out = { ...pos, lat: mLat, lng: mLng };
      if (pos.nextLat != null && pos.nextLng != null) {
        const [nLat, nLng] = mirrorPt(pos.nextLat, pos.nextLng);
        out.nextLat = nLat;
        out.nextLng = nLng;
      }
      return out;
    });
  }, [carPositions, gpsCenter, mirrorX, mirrorY]);

  // Apply mirroring to focusBounds
  const mirroredFocusBounds = useMemo(() => {
    if (!mirrorX && !mirrorY) return focusBounds;
    if (!gpsCenter || !focusBounds) return focusBounds;
    const [mLat1, mLng1] = mirrorPt(focusBounds.minLat, focusBounds.minLng);
    const [mLat2, mLng2] = mirrorPt(focusBounds.maxLat, focusBounds.maxLng);
    return {
      minLat: Math.min(mLat1, mLat2), maxLat: Math.max(mLat1, mLat2),
      minLng: Math.min(mLng1, mLng2), maxLng: Math.max(mLng1, mLng2),
    };
  }, [focusBounds, gpsCenter, mirrorX, mirrorY]);

  const selectableChannels = useMemo(() => {
    const speedCh = ALL_CHANNELS.find(ch => ch.key === 'speed');
    const others = availableChannels.length > 0 ? availableChannels : [];
    if (!speedCh) return others;
    return [speedCh, ...others.filter(ch => ch.key !== 'speed')];
  }, [availableChannels]);

  const activeChannel = selectableChannels.find(ch => ch.key === dataKey) || selectableChannels[0];
  const unit = activeChannel?.unit || '';
  const channelLabel = activeChannel?.label || 'Speed';

  // Compute center from first lap data (using mirrored coords)
  const center = useMemo(() => {
    const src = mirroredLaps || laps;
    if (!src?.length) return [39.8, -98.5];
    for (const lap of src) {
      if (!lap.data?.length) continue;
      const mid = lap.data[Math.floor(lap.data.length / 2)];
      if (mid?.lat != null && mid?.lng != null) return [mid.lat, mid.lng];
    }
    return [39.8, -98.5];
  }, [mirroredLaps, laps]);

  // Value range for legend (across all laps)
  const valueRange = useMemo(() => {
    if (!laps?.length) return null;
    let min = Infinity, max = -Infinity;
    for (const lap of laps) {
      if (!lap.data?.length) continue;
      for (const pt of lap.data) {
        const v = pt[dataKey];
        if (v != null) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    return isFinite(min) ? { min, max } : null;
  }, [laps, dataKey]);

  if (!laps?.length) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-dim)' }}>
        No lap data available
      </div>
    );
  }

  return (
    <div>
      {/* Channel selector */}
      <div style={styles.header}>
        <select
          data-doc-id="analysis-satellite-channel"
          value={dataKey}
          onChange={(e) => onDataKeyChange?.(e.target.value)}
          style={styles.dropdown}
        >
          {selectableChannels.map((ch) => (
            <option key={ch.key} value={ch.key}>
              {ch.label} Map{ch.unit ? ` (${ch.unit})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Map */}
      <div style={{
        borderRadius: 8,
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        height: 500,
      }}>
        <MapContainer
          center={center}
          zoom={15}
          style={{ width: '100%', height: '100%' }}
          preferCanvas={true}
          attributionControl={false}
          scrollWheelZoom={false}
        >
          <TileLayer url={ESRI_TILE_URL} maxZoom={19} />
          <MapLayers
            laps={mirroredLaps}
            dataKey={dataKey}
            carPositions={mirroredCarPositions}
            focusBounds={mirroredFocusBounds}
          />
        </MapContainer>
      </div>

      {/* Legend */}
      <div style={{ marginTop: 8, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Color scale */}
        {valueRange && (
          <div style={{ flex: 1 }}>
            <div style={{
              height: 10,
              borderRadius: 4,
              background: getLegendGradientCSS('absolute'),
            }} />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 4,
              fontFamily: 'var(--font-body)',
              fontSize: 10,
              color: 'var(--color-text-dim)',
            }}>
              <span>{valueRange.min.toFixed(0)}{unit ? ` ${unit}` : ''}</span>
              <span style={{ fontSize: 9, letterSpacing: 0.5 }}>{channelLabel}</span>
              <span>{valueRange.max.toFixed(0)}{unit ? ` ${unit}` : ''}</span>
            </div>
          </div>
        )}
        {/* Lap legend (multi-lap) */}
        {laps.length > 1 && <div style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          {laps.map((lap, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontFamily: 'var(--font-body)',
              fontSize: 10,
              color: 'var(--color-text-dim)',
            }}>
              <span style={{
                display: 'inline-block',
                width: 14,
                height: 3,
                borderRadius: 1,
                background: TRACE_COLORS[i % TRACE_COLORS.length],
              }} />
              {lap.driverLabel ? `${lap.driverLabel} ` : ''}L{lap.lapNum}
            </div>
          ))}
        </div>}
      </div>
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
  },
  dropdown: {
    background: '#0D1B2A',
    color: 'var(--color-text, #E0E0E0)',
    border: '1px solid rgba(138,180,200,0.25)',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 13,
    fontFamily: 'var(--font-heading, inherit)',
    cursor: 'pointer',
    outline: 'none',
  },
};
